/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-10-30 14:19:12                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-23 16:38:41                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * CustomersTable: Customer table section
*/

/* Imports */
import { Tooltip, Typography } from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
/***/

/* Components */
import { Table } from "../../../components/table/Table";
import CustomerDisplay from "../../../components/data/customer/CustomerDisplay";
import AddressDisplay from "../../../components/data/address/AddressDisplay";
import CustomerStatusBadge from "../../../components/data/customer-status/CustomerStatusBadge";
import PayerStatusBadge from "../../../components/data/payer-status/PayerStatusBadge";
import { OverflowTooltip } from "../../../components/table/TableCellTooltip";
import { hasExternalId, isOdooConnected } from "../../../services/odoo/odoo";
import { t } from "../../../i18n";
/***/

/**
* CustomersTable: Customer table section
* Display customers in table
*/
export default function CustomersTable({
  customers,
  selectedHead,
  orderDesc,
  onHeadClick
}: {
  customers: EasyTech.Customer[],
  selectedHead?: string,
  orderDesc?: boolean,
  onHeadClick?: (head: string, orderDesc: boolean) => void
}) {
  const navigate = useNavigate();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1440);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 1440);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const TABLE_HEAD = [
    {
      label: t("common:name"),
      orderKey: "name",
      width: "20%"
    }, {
      label: t("common:nip"),
      orderKey: "nip",
      width: "20%"
    }, {
      label: t("common:email"),
      orderKey: "contactInfo.mail",
      width: "10%"
    }, {
      label: t("common:phone"),
      orderKey: "contactInfo.phone",
      width: "10%"
    },
    {
      label: t("common:type"),
      orderKey: "badPayer",
      width: "10%",
      headClassName: isSmallScreen ? "text-center" : ""
    },
    {
      label: t("common:status"),
      orderKey: "type",
      width: "10%",
      headClassName: isSmallScreen ? "text-center" : ""
    },
    {
      label: t("common:address"),
      orderKey: "address.city",
      width: "20%"
    }
  ];

  /*
   * L'identifiant Odoo n'existe que sur un realm relié à Odoo, comme dans l'onglet Informations de
   * la fiche client. Non triable : le tri part à l'API sur `search`, et rien ne dit qu'elle accepte
   * ce champ — proposer un en-tête cliquable qui échoue serait pire que pas d'en-tête cliquable.
   */
  const showOdooId = isOdooConnected();

  if (showOdooId) {
    TABLE_HEAD.push({
      label: t("client:odoo:externalId:label"),
      orderKey: null as unknown as string,
      width: "10%"
    });
  }

  return (
    <Table
      tableHead={TABLE_HEAD}
      emptyTable={(!customers || customers.length === 0) ? "No Customer" : false}
      selectedHead={selectedHead}
      orderDesc={orderDesc}
      onHeadClick={onHeadClick}
      enableRowLinks={true}
    >
      {customers.map((customer, index) => {
        const isLast = index === customers.length - 1;
        const classes = isLast ? "p-2 align-middle" : "p-2 border-b border-blue-gray-50 align-middle";
        const customerUrl = `/customers/${customer.id}`;

        const emailText = customer.contactInfo && customer.contactInfo.email ? customer.contactInfo.email : t("common:noEmail");
        const phoneText = customer.contactInfo ? customer.contactInfo.phone : t("common:noPhone");

        return (
          <tr
            key={"customer_" + index}
            onClick={() => navigate(customerUrl)}
            data-href={customerUrl}
          >
            {/* NAME */}
            <td className={classes}>
              <div className="et-cell-wrap">
                <CustomerDisplay customer={customer} noToolTip />
              </div>
            </td>

            {/* NIP */}
            <td className={classes}>
              <div className="et-cell-wrap">
                <Typography variant="small" color="blue-gray">
                  {customer.nip || ""}
                </Typography>
              </div>
            </td>

            {/* EMAIL */}
            <td className={classes}>
              <OverflowTooltip content={emailText} className="et-cell-truncate text-sm text-blue-gray-900">
                <div>
                  {emailText}
                </div>
              </OverflowTooltip>
            </td>

            {/* PHONE */}
            <td className={classes}>
              <OverflowTooltip content={phoneText} className="et-cell-truncate text-sm text-blue-gray-900">
                <div>
                  {phoneText}
                </div>
              </OverflowTooltip>
            </td>

            {/* BAD PAYER (Renamed to TYPE) */}
            <td className={classes}>
              <div className={isSmallScreen ? "et-cell-centered" : ""}>
                {isSmallScreen ? (
                  <Tooltip content={customer.badPayer ? t("common:badPayer") : t("common:goodPayer")}>
                    <div>
                      <PayerStatusBadge badPayer={customer.badPayer} iconOnly />
                    </div>
                  </Tooltip>
                ) : (
                  <PayerStatusBadge badPayer={customer.badPayer} />
                )}
              </div>
            </td>

            {/* STATUS */}
            <td className={classes}>
              <div className={isSmallScreen ? "et-cell-centered" : ""}>
                {customer.type && (
                  isSmallScreen ? (
                    <Tooltip content={t(`client:type.${customer.type.toLowerCase()}`)}>
                      <div>
                        <CustomerStatusBadge status={customer.type} iconOnly />
                      </div>
                    </Tooltip>
                  ) : (
                    <CustomerStatusBadge status={customer.type} />
                  )
                )}
              </div>
            </td>

            {/* ADDRESS */}
            <td className={classes}>
              <div className="et-cell-wrap">
                {customer.address && (
                  <AddressDisplay address={customer.address} size="small" noToolTip />
                )}
              </div>
            </td>

            {/* ODOO ID */}
            {showOdooId && (
              <td className={classes}>
                <div className="et-cell-wrap">
                  <Typography variant="small" color="blue-gray">
                    {hasExternalId(customer.externalId)
                      ? customer.externalId
                      : t("client:odoo:externalId:notSynced")}
                  </Typography>
                </div>
              </td>
            )}
          </tr>
        );
      })}
    </Table>
  );
}
/***/
